import type { OrderProduct, ReplacementEntry } from "./types";
import { Clipboard } from "lucide-react";

interface PatientOrderSidebarProps {
  products: OrderProduct[];
  replacements?: Record<string, ReplacementEntry>;
  totalAmount?: string;
  actionButton?: React.ReactNode;
  ordernote?: string | null;
}

const PatientOrderSidebar: React.FC<PatientOrderSidebarProps> = ({ products, replacements, totalAmount, actionButton, ordernote }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 sticky top-4">
      {ordernote && (
        <div className="p-3 m-3 mb-0 bg-amber-50/80 border border-amber-200 rounded-xl">
          <div className="flex items-center gap-1.5 mb-1 text-amber-700">
            <Clipboard size={14} className="flex-shrink-0" />
            <span className="text-[11px] font-bold uppercase tracking-wider">Order Note</span>
          </div>
          <p className="text-xs text-amber-900 leading-relaxed font-medium">
            {ordernote}
          </p>
        </div>
      )}
      <div className="px-4 py-3 border-b border-slate-100">
        <h3 className="text-sm font-bold text-slate-800">
          Patient Order List ({products.length} Items)
        </h3>
      </div>
      <div className="px-4 py-2">
        <div className="flex items-center justify-between text-[0.65rem] font-semibold text-slate-400 uppercase tracking-wider mb-2">
          <span>Health Product</span>
          <span>Quantity</span>
        </div>
        <div className="max-h-[400px] overflow-y-auto space-y-1.5">
          {products.map((product) => {
            const replacement = replacements?.[product.productId];
            const isReplaced = !!replacement;
            const originalPrice = parseFloat(product.productPrice) || 0;
            const substitutePrice = isReplaced ? replacement.substitutePrice : originalPrice;

            return (
              <div key={product.productId} className="py-2 border-b border-slate-50 last:border-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-2">
                    <img
                      src={isReplaced ? replacement.substituteImage : product.productImage}
                      alt={isReplaced ? replacement.substituteName : product.name}
                      className="w-7 h-7 rounded-md object-cover bg-slate-100 border border-slate-100 mt-0.5"
                      onError={(e) => { (e.target as HTMLImageElement).src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23cbd5e1'%3E%3Crect width='24' height='24' rx='4'/%3E%3C/svg%3E"; }}
                    />
                    <div>
                      <div className="text-xs text-slate-700 font-semibold max-w-[130px] truncate" title={isReplaced ? replacement.substituteName : product.name}>
                        {isReplaced ? replacement.substituteName : product.name}
                      </div>
                      {isReplaced ? (
                        <div className="text-[0.65rem] text-slate-400 mt-0.5">
                          <span className="line-through mr-1">₹{originalPrice.toFixed(2)}</span>
                          <span className="text-emerald-600 font-semibold">₹{substitutePrice.toFixed(2)}</span>
                        </div>
                      ) : (
                        <div className="text-[0.65rem] text-slate-400 mt-0.5">
                          ₹{originalPrice.toFixed(2)}
                        </div>
                      )}
                      {isReplaced && replacement.substituteNote && (
                        <div className="text-[0.6rem] text-red-500 font-medium leading-tight max-w-[130px] mt-1 bg-red-50/50 border border-red-100 rounded px-1.5 py-0.5 break-words">
                          Note: {replacement.substituteNote}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-bold text-slate-800">x{product.quantity}</span>
                    <div className="text-[0.68rem] font-bold text-slate-700 mt-0.5">
                      ₹{(substitutePrice * product.quantity).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Total + Action */}
      {(totalAmount || actionButton) && (
        <div className="px-4 py-3 border-t border-slate-100">
          {totalAmount && (() => {
            const originalTotal = parseFloat(totalAmount) || 0;
            let netDiff = 0;
            products.forEach((product) => {
              const replacement = replacements?.[product.productId];
              if (replacement) {
                const originalPrice = parseFloat(product.productPrice) || 0;
                const substitutePrice = replacement.substitutePrice || 0;
                netDiff += (substitutePrice - originalPrice) * product.quantity;
              }
            });
            const newTotal = originalTotal + netDiff;

            return (
              <div className="space-y-1.5 mb-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 font-semibold">Total Amount</span>
                  {netDiff !== 0 ? (
                    <div className="text-right">
                      <span className="text-xs text-slate-400 line-through mr-2">₹{originalTotal.toFixed(2)}</span>
                      <span className="text-base font-bold text-emerald-600">₹{newTotal.toFixed(2)}</span>
                    </div>
                  ) : (
                    <span className="text-base font-bold text-emerald-600">₹{originalTotal.toFixed(2)}</span>
                  )}
                </div>
                {netDiff !== 0 && (
                  <div className="flex items-center justify-between text-[0.7rem]">
                    <span className="text-slate-400 font-medium">Difference</span>
                    {netDiff < 0 ? (
                      <span className="text-emerald-600 font-bold bg-emerald-50 border border-emerald-100 rounded px-1.5 py-0.5">
                        ₹{Math.abs(netDiff).toFixed(2)} Reduced
                      </span>
                    ) : (
                      <span className="text-red-500 font-bold bg-red-50 border border-red-100 rounded px-1.5 py-0.5">
                        ₹{Math.abs(netDiff).toFixed(2)} Increased
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          {actionButton}
        </div>
      )}
    </div>
  );
};

export default PatientOrderSidebar;
